import { Cesium } from 'app/cesium';
import { Decimal } from 'decimal.js';
import { Position } from 'app/agriculture/queries/types/geometry/Position';

export interface LatLon {
    latitude: number;
    longitude: number;
}

export function positionToLatLon([longitude, latitude]: Position): LatLon {
    return { latitude, longitude };
}

interface Cartesian3 {
    x: number;
    y: number;
    z: number;
    clone?: () => Cartesian3;
    equals?: (v: Cesium.Cartesian3) => boolean;
}

export function cartographicPolygonCentroid(positions: LatLon[]): LatLon {
    const length = positions.length;
    if (length < 3) {
        return null;
    }

    let centerLon = new Decimal(0);
    let centerLat = new Decimal(0);
    let area = new Decimal(0);

    for (let i = 0; i < length; i++) {
        const pos = positions[i];
        const nextPos = positions[i + 1] || positions[0];

        const posLat = new Decimal(pos.latitude);
        const posLon = new Decimal(pos.longitude);
        const nextPosLat = new Decimal(nextPos.latitude);
        const nextPosLon = new Decimal(nextPos.longitude);

        const signedArea = posLat.times(nextPosLon).minus(nextPosLat.times(posLon));

        centerLon = centerLon.plus(posLon.plus(nextPosLon).times(signedArea));
        centerLat = centerLat.plus(posLat.plus(nextPosLat).times(signedArea));
        area = area.plus(signedArea);
    }

    area = area.dividedBy(2);

    return {
        longitude: centerLon.dividedBy(area.times(6)).toNumber(),
        latitude: centerLat.dividedBy(area.times(6)).toNumber(),
    };
}

export function cartesian3ToDegrees(cartesian3: Cesium.Cartesian3): LatLon {
    return cartographicToDegrees(Cesium.Ellipsoid.WGS84.cartesianToCartographic(cartesian3));
}

export function cartographicToDegrees(cartographic: Cesium.Cartographic): LatLon {
    return {
        longitude: Cesium.Math.toDegrees(cartographic.longitude),
        latitude: Cesium.Math.toDegrees(cartographic.latitude),
    };
}

/**
 * Converts a cartesian to a cartographic; adds some height in meters; converts back to a cartesian
 */
export function adjustHeightOfCartesian(
    cartesian: Cesium.Cartesian3,
    heightAdjustmentInMeters: number,
): Cesium.Cartesian3 {
    if (!cartesian) {
        return null;
    }
    const cartographic = Cesium.Cartographic.fromCartesian(cartesian) as Cesium.Cartographic;
    return new Cesium.Cartesian3.fromRadians(
        cartographic.longitude,
        cartographic.latitude,
        cartographic.height + heightAdjustmentInMeters,
    );
}

/**
 * Moves a cartesian down by a bunch - very poor man's clamp to ground - by shoving a point
 * into/below the ground.
 */
export function slamCartesianFurtherIntoGround(cartesian: Cesium.Cartesian3): Cesium.Cartesian3 {
    // poor man's clamp-to-ground.. otherwise it'll hover a little tiny bit off the ground due
    // to interpolation. We really need clamp relative to ground.
    // TODO we need clamp relative to ground / globe height picking on frame
    return adjustHeightOfCartesian(cartesian, -100);
}
